/**
 * cytoscape 视觉动画工具。
 *
 * 不使用全局 transition，避免流式事件密集到达时反复重启动画造成闪烁；
 * 改为在状态真正变化时按元素播放短促的直接动画，并且禁用动画队列。
 */

export type CytoscapeStyle = Record<string, unknown>;

const NODE_ANIMATION_MS = 220;
const EDGE_ANIMATION_MS = 180;
const IMMEDIATE_STYLE_NAMES = new Set([
  'label',
  'font-size',
  'font-weight',
  'font-family',
  'text-wrap',
  'text-max-width',
  'text-valign',
  'text-halign',
  'text-justification',
  'line-height',
]);

/** 平滑写入节点最终样式；默认不做 pulse，避免密集流式事件下边框反复加粗。 */
export function applyNodeStyleSmooth(
  node: any,
  style: CytoscapeStyle,
  options: { pulse?: boolean; fallbackBorderWidth?: number } = {}
) {
  const { pulse = false, fallbackBorderWidth = 1.4 } = options;
  const originalBorderWidth =
    typeof style['border-width'] === 'number'
      ? (style['border-width'] as number)
      : Number(node.style('border-width')) || fallbackBorderWidth;

  node.stop(true, false);
  const runPulse = () => {
    if (!pulse) return;

    node.animate(
      { style: { 'border-width': originalBorderWidth + 1.1 } },
      {
        duration: 90,
        easing: 'ease-out-cubic',
        queue: false,
        complete: () => {
          node.animate(
            { style: { 'border-width': originalBorderWidth } },
            {
              duration: 140,
              easing: 'ease-in-out-cubic',
              queue: false,
            }
          );
        },
      }
    );
  };

  const { animatableStyle, immediateStyle } = splitStyleByAnimationSafety(
    node,
    style,
    'node'
  );
  if (Object.keys(immediateStyle).length > 0) {
    node.style(immediateStyle);
  }
  if (Object.keys(animatableStyle).length === 0) {
    runPulse();
    return;
  }

  try {
    node.animate(
      { style: animatableStyle },
      {
        duration: NODE_ANIMATION_MS,
        easing: 'ease-in-out-cubic',
        queue: false,
        complete: runPulse,
      }
    );
  } catch (err) {
    // Cytoscape 偶发无法创建样式动画时只降级当前元素，避免打断整张画布交互。
    console.warn('[cytoscapeVisual] node style animation failed', err);
    node.style(animatableStyle);
  }
}

/** 平滑写入边的最终样式，禁用队列避免连续流事件积压。 */
export function applyEdgeStyleSmooth(edge: any, style: CytoscapeStyle) {
  edge.stop(true, false);
  const { animatableStyle, immediateStyle } = splitStyleByAnimationSafety(
    edge,
    style,
    'edge'
  );
  if (Object.keys(immediateStyle).length > 0) {
    edge.style(immediateStyle);
  }
  if (Object.keys(animatableStyle).length === 0) return;

  try {
    edge.animate(
      { style: animatableStyle },
      {
        duration: EDGE_ANIMATION_MS,
        easing: 'ease-in-out-cubic',
        queue: false,
      }
    );
  } catch (err) {
    // 边线动画失败时保留最终点亮状态，不让异常冒泡影响画布拖拽。
    console.warn('[cytoscapeVisual] edge style animation failed', err);
    edge.style(animatableStyle);
  }
}

/**
 * Cytoscape 的 style 动画在异步 step 阶段要求每个属性都有 startStyle。
 * 暂停弹窗期间恢复执行时，少数属性可能取不到起始样式；这里把这类属性直接落最终值，
 * 其它属性仍保留原本的 Cytoscape 平滑动画，避免影响点亮效果。
 */
function splitStyleByAnimationSafety(
  element: any,
  style: CytoscapeStyle,
  source: 'node' | 'edge'
) {
  const animatableStyle: CytoscapeStyle = {};
  const immediateStyle: CytoscapeStyle = {};
  const skipped: string[] = [];

  Object.entries(style).forEach(([name, value]) => {
    if (value === undefined || value === null) return;
    const dashName = toDashName(name);
    if (IMMEDIATE_STYLE_NAMES.has(dashName)) {
      immediateStyle[name] = value;
      return;
    }
    const startStyle = getStartStyle(element, name);
    if (startStyle?.name) {
      animatableStyle[name] = value;
    } else {
      immediateStyle[name] = value;
      skipped.push(name);
    }
  });

  if (skipped.length > 0) {
    console.warn('[cytoscapeVisual] skip unsafe style animation', {
      source,
      id: element?.id?.(),
      skipped,
    });
  }

  return { animatableStyle, immediateStyle };
}

function getStartStyle(element: any, name: string) {
  try {
    const dashName = toDashName(name);
    const rawStyle = element?.pstyle?.(name) || element?.pstyle?.(dashName);
    if (!rawStyle) return null;
    const cyStyle = element?.cy?.()?.style?.();
    if (rawStyle?.strValue !== undefined && cyStyle?.parse) {
      return cyStyle.parse(dashName, rawStyle.strValue);
    }
    return rawStyle?.name ? rawStyle : null;
  } catch {
    return null;
  }
}

function toDashName(name: string) {
  return name.replace(/[A-Z]/g, char => `-${char.toLowerCase()}`);
}
